import { useMemo, type JSX } from 'react';
import type {
  AnswerCombinationRankingItemType,
  QuestionAnswerDistributionType,
} from 'types/api/diagnosisReportRefResponseType';

import '../../assets/styles/ws-report.scss';
import { formatNumberToCurrency } from 'utils/app';
import {
  buildPatternExpander,
  formatPatternFallback,
} from './wsReportAnswerCombinationRanking.utils';

/**
 * 回答パターンランキング（同じ選択の組み合わせを選んだ人数の多い順）を表示するコンポーネント。
 * 1パターン＝1カードで質問文・回答内容を縦に表示。同じ回答者数は同順位。
 * 診断・クイズ両方のレポートで利用する。
 */
export default function WsReportAnswerCombinationRanking(props: {
  answerCombinationRanking: AnswerCombinationRankingItemType[];
  /** 質問別分布 */
  questionAnswerDistribution?: QuestionAnswerDistributionType[] | null;
}): JSX.Element {
  const { answerCombinationRanking: ranking, questionAnswerDistribution: questions } = props;

  /**
   * パターン "A-B-C" を質問文・選択肢テキストの配列に展開
   */
  const expandPattern = useMemo(() => buildPatternExpander(questions), [questions]);

  return (
    <section className="section-chart section-answer-combination-ranking">
      <div className="card card-chart">
        <div className="card-header">
          <h3 className="card-title">回答パターンランキング</h3>
          <p className="card-subtitle">
            同じ選択肢の組み合わせを選んだ人数の多い順（全設問に回答した方のみ集計）
          </p>
        </div>
        <div className="card-body pt-3">
          {ranking.length > 0 ? (
            <div className="answer-combination-ranking-cards">
              {ranking.map(item => {
                const details = expandPattern(item.pattern);
                return (
                  <div
                    key={`${item.rank}-${item.pattern}`}
                    className="answer-combination-ranking-card"
                  >
                    <div className="answer-combination-ranking-card-header">
                      <span className="answer-combination-ranking-card-rank">{item.rank}位</span>
                      <span className="answer-combination-ranking-card-stats">
                        {formatNumberToCurrency(item.count)}名（{item.percentage}%）
                      </span>
                    </div>
                    <div className="answer-combination-ranking-card-body">
                      {details && details.length > 0 ? (
                        <ul className="answer-combination-ranking-detail-list">
                          {details.map((d, i) => (
                            <li key={i} className="answer-combination-ranking-detail-item">
                              <span className="detail-question">{d.questionTitle}</span>
                              <span className="detail-answer">
                                選択肢{d.optionNo}：{d.optionTitle}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="answer-combination-ranking-pattern-fallback">
                          {formatPatternFallback(item.pattern)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center p-4">
              <p className="mb-0">データがありません</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
